// A recipe (WP5.2): its versions newest first (drafts waiting for the baker, the one in use,
// retired ones folded away), what each uses at full precision with its precision warnings,
// the statement, and what the product costs. Drafts are edited, signed off and retired here.

import type { RecipeDetail, RecipeVersionDetail } from '@enp/domain/api';
import { newId } from '@enp/domain/ids';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  EmptyState,
  Money,
  PageHeader,
  Skeleton,
  StatusBadge,
  toast,
  type Status,
} from '@enp/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  Archive,
  ArrowLeft,
  BookOpen,
  ClipboardCheck,
  CopyPlus,
  PackageSearch,
  Pencil,
  Sigma,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { RECIPE_STATUS_LABEL, ROLE_LABEL, ROLE_ORDER } from '../../../../catalogue/labels.js';
import { ApiError } from '../../../../lib/api.js';
import { AllergenChips, Note, Panel, useCan } from '../../../../production/parts.js';
import {
  createVersion,
  invalidateProduction,
  recipeQuery,
} from '../../../../production/queries.js';
import {
  RetireDialog,
  SignOffDialog,
  VersionLines,
} from '../../../../production/recipe-dialogs.js';
import {
  COST_SOURCE_LABEL,
  costChangeText,
  coverageSentence,
  isPersonCost,
  sentence,
  statementNamesNonIngredients,
  undeclaredAllergens,
  undeclaredText,
  coverageStatus,
  refusalText,
  rollupBatchCents,
  STATEMENT_STALE,
  staleStatementText,
  unitCostText,
  yieldText,
  blocksSignOff,
} from '../../../../production/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { actionProblem } from '../../../../stock-control/parts.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/recipes/$recipeId/')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.recipeId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(recipeQuery(params.recipeId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: RecipePage,
  pendingComponent: RecipeSkeleton,
  errorComponent: RecipeError,
  notFoundComponent: RecipeNotFound,
});

const VERSION_STATUS: Record<RecipeVersionDetail['status'], Status> = {
  active: 'success',
  draft: 'warning',
  retired: 'neutral',
};

const ORIGIN_LABEL: Record<RecipeVersionDetail['origin'], string> = {
  import: 'Brought in by the catalogue import',
  person: 'Written here',
  system: 'Set up with the account',
};

function Crumbs({ name }: { name: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/recipes">Recipes</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{name}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

type Dialog = { kind: 'sign-off' | 'retire'; version: RecipeVersionDetail } | null;

function VersionMeta({ v }: { v: RecipeVersionDetail }) {
  const parts: ReactNode[] = [ORIGIN_LABEL[v.origin]];
  if (v.last_edited_at) {
    parts.push(
      <>
        last changed{v.last_edited_by?.name ? ` by ${v.last_edited_by.name}` : ''}{' '}
        <AccountDate value={v.last_edited_at} />
      </>,
    );
  }
  if (v.signed_off_at) {
    parts.push(
      <>
        signed off{v.signed_off_by?.name ? ` by ${v.signed_off_by.name}` : ''}{' '}
        <AccountDate value={v.signed_off_at} />
      </>,
    );
  }
  if (v.retired_at) {
    parts.push(
      <>
        retired <AccountDate value={v.retired_at} />
      </>,
    );
  }
  return (
    <p className="text-sm text-muted">
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          {p}
        </span>
      ))}
    </p>
  );
}

/** The lines a draft can't be signed off with yet: a unit with no conversion (UI5A-102). */
const stuckLines = (v: Pick<RecipeVersionDetail, 'lines'>): string[] =>
  v.lines
    .filter((l) => l.precision_warnings.some((w) => w.code === 'missing_conversion'))
    .map((l) => l.component.name);

/** "Edit draft" in the page header: filled when it leads, outlined beside sign-off. */
function EditDraftButton({
  recipeId,
  draft,
  primary,
}: {
  recipeId: string;
  draft: RecipeVersionDetail;
  primary: boolean;
}) {
  return (
    <Button variant={primary ? 'primary' : 'secondary'} asChild>
      <Link to="/recipes/$recipeId/versions/$versionId" params={{ recipeId, versionId: draft.id }}>
        <Pencil aria-hidden />
        Edit draft
      </Link>
    </Button>
  );
}

function VersionBlock({
  recipe,
  v,
  canEdit,
  canSignOff,
  onDialog,
}: {
  recipe: RecipeDetail;
  v: RecipeVersionDetail;
  canEdit: boolean;
  canSignOff: boolean;
  onDialog: (d: Dialog) => void;
}) {
  const headingId = `version-${v.version_no}`;
  return (
    <li className="relative flex gap-4 pb-8 last:pb-0">
      {/* The timeline: a dot per version on a line. */}
      <span aria-hidden className="absolute left-[7px] top-5 h-full w-px bg-border" />
      <span
        aria-hidden
        className={`relative mt-1.5 size-4 shrink-0 rounded-full border-2 ${
          v.status === 'active'
            ? 'border-state-success bg-state-success'
            : v.status === 'draft'
              ? 'border-state-warning bg-surface-raised'
              : 'border-border-strong bg-surface-sunken'
        }`}
      />
      <article aria-labelledby={headingId} className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h3 id={headingId} className="font-display text-base font-semibold text-fg">
                Version {v.version_no}
              </h3>
              <StatusBadge status={VERSION_STATUS[v.status]}>
                {v.status === 'active' ? 'In use' : RECIPE_STATUS_LABEL[v.status]}
              </StatusBadge>
              <span className="text-sm text-fg">
                {yieldText(v.batch_yield_qty, v.yield_uom)} per batch
              </span>
            </div>
            <VersionMeta v={v} />
          </div>
          {/* Row actions are outlined: the page header holds the one filled action (UI8C-4). */}
          <div className="flex flex-wrap items-center gap-2">
            {v.status === 'draft' && canEdit && (
              <Button variant="secondary" size="sm" asChild>
                <Link
                  to="/recipes/$recipeId/versions/$versionId"
                  params={{ recipeId: recipe.id, versionId: v.id }}
                >
                  <Pencil aria-hidden />
                  Edit
                </Link>
              </Button>
            )}
            {v.status === 'draft' && canSignOff && (
              <Button
                size="sm"
                variant="secondary"
                icon={<ClipboardCheck aria-hidden />}
                onClick={() => onDialog({ kind: 'sign-off', version: v })}
              >
                Sign off…
              </Button>
            )}
            {v.status !== 'retired' && canEdit && (
              <Button
                variant="ghost"
                size="sm"
                icon={<Archive aria-hidden />}
                aria-label={`Retire version ${v.version_no}…`}
                onClick={() => onDialog({ kind: 'retire', version: v })}
              >
                Retire…
              </Button>
            )}
          </div>
        </div>

        {v.status === 'draft' && (
          <Note>
            <strong className="font-semibold">Waiting for the baker’s sign-off.</strong> A draft
            isn’t used for production until it’s signed off.
            {(() => {
              // Only the checks: what blocks sign-off is said apart, below (UI8C-5).
              const checks = v.lines.reduce(
                (n, l) => n + l.precision_warnings.filter((w) => !blocksSignOff(w)).length,
                0,
              );
              return checks > 0
                ? ` ${checks === 1 ? '1 quantity is' : `${checks} quantities are`} marked “Check” below.`
                : '';
            })()}{' '}
            {(() => {
              // Said before the dialog opens, too (UI5A-102).
              const stuck = stuckLines(v);
              return stuck.length > 0 ? (
                <strong className="block pt-1 font-semibold text-state-danger">
                  It can’t be signed off yet: {stuck.join(' and ')}{' '}
                  {stuck.length === 1 ? 'is' : 'are'} in a unit with no conversion to how{' '}
                  {stuck.length === 1 ? 'it’s' : 'they’re'} stocked.
                </strong>
              ) : null;
            })()}
          </Note>
        )}
        {v.status === 'active' && v.open_batch_count > 0 && (
          <p className="text-sm text-muted">
            {v.open_batch_count === 1
              ? '1 planned batch is pinned to this version.'
              : `${v.open_batch_count} planned batches are pinned to this version.`}
          </p>
        )}

        <VersionLines version={v} label={`Ingredients, version ${v.version_no}`} />

        {v.instructions && (
          <p className="text-body text-fg">
            <span className="font-medium">Method: </span>
            {v.instructions}
          </p>
        )}
        <div className="flex flex-col gap-1 text-body">
          <span className="font-medium text-fg">
            {v.status === 'draft' ? 'Statement' : 'Signed statement'}
          </span>
          {v.allergen_statement ? (
            <p className="text-fg">{v.allergen_statement}</p>
          ) : (
            <p className="text-muted">
              Not written yet. The statement generated from the ingredients is offered at sign-off:
              “{v.generated_statement.text}”
            </p>
          )}
          {v.status === 'active' && v.statement_check?.out_of_date && (
            <Note>
              <strong className="font-semibold">{STATEMENT_STALE}.</strong>{' '}
              {staleStatementText(v.statement_check.missing_allergens)}
            </Note>
          )}
          {v.status !== 'retired' &&
            (() => {
              // UI5A-2: packaging named in a statement reads as something you eat on a label.
              const named = statementNamesNonIngredients(v.allergen_statement, v);
              return named.length > 0 ? (
                <Note>
                  <strong className="font-semibold">The statement names packaging.</strong>{' '}
                  {named.join(', ')} {named.length === 1 ? 'isn’t' : 'aren’t'} an ingredient, but
                  the statement lists {named.length === 1 ? 'it' : 'them'}. Labels print it:{' '}
                  {v.status === 'draft'
                    ? 'use the generated statement at sign-off.'
                    : 'make a new version and have the baker sign off a corrected statement.'}
                </Note>
              ) : null;
            })()}
        </div>
      </article>
    </li>
  );
}

function CostPanel({ recipe }: { recipe: RecipeDetail }) {
  const cost = recipe.cost;
  const base = recipe.product.base_uom;
  const cents = cost.stored.standard_cost_cents;
  // One batch, summed line by line (never a rounded unit cost × yield, UI5A-8).
  const batch = rollupBatchCents(cost);
  const coverage = coverageStatus(cost.coverage, recipe.product.name);
  const version = cost.recipe_version;
  // A draft waiting while another version is in use: what signing it off would do (UI5A-5).
  const waiting = recipe.versions.find(
    (v) =>
      v.status === 'draft' &&
      v.id !== version?.id &&
      (v.cost_if_signed?.unit_cents ?? null) !== null,
  );
  return (
    <Panel
      id="cost"
      title="Standard cost"
      actions={
        <Button variant="secondary" size="sm" asChild>
          <Link to="/recipes/costs/$productId" params={{ productId: recipe.product.id }}>
            <Sigma aria-hidden />
            How it adds up
          </Link>
        </Button>
      }
    >
      {cents === null ? (
        // Which version it would come from, in the same breath (UI5A-11): never "Costed from
        // draft version 1" next to "Not worked out yet".
        <p className="text-body text-muted" data-testid="cost-version">
          Not worked out yet
          {version
            ? version.status === 'active'
              ? ` from version ${version.version_no}, in use`
              : ` from draft version ${version.version_no}`
            : ''}
          .{' '}
          {cost.computed.gap
            ? `${sentence(refusalText(cost.computed.gap.message))}.`
            : 'Roll up the costs to work it out.'}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          <p className="font-display text-2xl font-semibold text-fg">{unitCostText(cents, base)}</p>
          {batch !== null && (
            <p className="text-body text-fg">
              <Money cents={batch} /> for one batch
              {cost.batch_yield_qty && cost.yield_uom
                ? ` (${yieldText(cost.batch_yield_qty, cost.yield_uom).toLowerCase()})`
                : ''}
            </p>
          )}
          {cost.stored.cost_source && (
            <p className="text-sm text-muted">{COST_SOURCE_LABEL[cost.stored.cost_source]}</p>
          )}
        </div>
      )}
      {version && cents !== null && (
        // Which version the cost comes from (UI5A-12, D-058: the one in use, else the latest
        // draft).
        <p className="text-sm text-fg" data-testid="cost-version">
          {version.status === 'active'
            ? `Costed from version ${version.version_no}, in use.`
            : `Costed from draft version ${version.version_no}: not signed off yet, so it may change.`}
        </p>
      )}
      <StatusBadge status={coverage.status}>{coverage.label}</StatusBadge>
      {cost.coverage.computed < cost.coverage.total && (
        <p className="text-sm text-fg">{coverageSentence(cost.coverage, recipe.product.name)}</p>
      )}
      {waiting?.cost_if_signed && waiting.cost_if_signed.unit_cents !== null && (
        <Note tone="neutral">
          If version {waiting.version_no} is signed off:{' '}
          {isPersonCost(cost.stored.cost_source) && cents !== null
            ? `the cost stays ${unitCostText(cents, base)} (set by a person); the recipe would give ${unitCostText(waiting.cost_if_signed.unit_cents, base)}.`
            : `${costChangeText(cents, waiting.cost_if_signed.unit_cents, base)}.`}
        </Note>
      )}
      {cost.computed.cents !== null && cost.computed.cents !== cents && (
        <Note tone="neutral">
          Worked out from the recipe now: {unitCostText(cost.computed.cents, base)}. The next
          roll-up stores it
          {cost.stored.cost_source === 'admin' || cost.stored.cost_source === 'manual'
            ? ', unless a person’s cost is kept'
            : ''}
          .
        </Note>
      )}
    </Panel>
  );
}

function RecipePage() {
  const { recipeId } = Route.useParams();
  const { data: recipe } = useSuspenseQuery(recipeQuery(recipeId));
  useDocumentTitle(`${recipe.product.name} recipe`);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCan('recipe.edit', true);
  const canSignOff = useCan('recipe.signoff', true);
  const [dialog, setDialog] = useState<Dialog>(null);

  const drafts = recipe.versions.filter((v) => v.status === 'draft');
  const current = recipe.versions.filter((v) => v.status !== 'retired');
  const retired = recipe.versions.filter((v) => v.status === 'retired');
  const latestDraft = drafts[0];
  // Known not to pass sign-off yet: "Edit draft" leads (UI5A-205).
  const draftBlocked = latestDraft !== undefined && stuckLines(latestDraft).length > 0;
  const signOffLeads = canSignOff && !(draftBlocked && canEdit);
  // What the ingredients of the version in use (else the latest draft) contain (UI5A-1).
  const costed = recipe.versions.find((v) => v.status === 'active') ?? latestDraft;
  const ingredients = costed?.generated_statement.allergens ?? [];
  const undeclared = undeclaredAllergens(recipe.product.allergens, ingredients);
  const roles = ROLE_ORDER.filter((r) => recipe.product.roles.includes(r));

  // One key per new draft (D-063, D-065): a double click makes one draft; the next one
  // after it succeeded is a new request.
  const attempt = useRef(newId());
  const newDraft = useMutation({
    mutationFn: () => createVersion(recipe.id, undefined, attempt.current),
    onSuccess: async (detail) => {
      attempt.current = newId();
      await invalidateProduction(queryClient);
      const made = detail.versions[0];
      toast.success(`Draft version ${made?.version_no ?? ''} made`, {
        description: 'A copy of the latest version. Change it, then ask the baker to sign it off.',
      });
      if (made) {
        void navigate({
          to: '/recipes/$recipeId/versions/$versionId',
          params: { recipeId: recipe.id, versionId: made.id },
        });
      }
    },
    onError: (error) => {
      const p = actionProblem(error, 'No draft was made');
      toast.error(p.title, { description: p.explain });
    },
  });

  return (
    <>
      <PageHeader
        eyebrow={<Crumbs name={recipe.product.name} />}
        title={recipe.product.name}
        description={
          <span className="flex flex-wrap items-center gap-1.5">
            {roles.map((role) => (
              <Badge key={role} tone={role === 'sellable' ? 'primary' : 'neutral'}>
                {ROLE_LABEL[role]}
              </Badge>
            ))}
            <Link
              to="/catalogue/$productId"
              params={{ productId: recipe.product.id }}
              className="ml-1 inline-flex items-center gap-1 text-sm font-medium text-primary-text underline-offset-4 hover:underline"
            >
              <BookOpen aria-hidden className="size-4" />
              Product in the catalogue
            </Link>
          </span>
        }
        actions={
          <>
            {/* The filled one goes last, at the right, like every other detail header. */}
            {latestDraft && canEdit && signOffLeads && (
              <EditDraftButton recipeId={recipe.id} draft={latestDraft} primary={false} />
            )}
            {latestDraft && canSignOff && (
              <Button
                variant={signOffLeads ? 'primary' : 'secondary'}
                icon={<ClipboardCheck aria-hidden />}
                onClick={() => setDialog({ kind: 'sign-off', version: latestDraft })}
              >
                Sign off version {latestDraft.version_no}…
              </Button>
            )}
            {latestDraft && canEdit && !signOffLeads && (
              <EditDraftButton recipeId={recipe.id} draft={latestDraft} primary />
            )}
            {!latestDraft && canEdit && (
              <Button
                icon={<CopyPlus aria-hidden />}
                loading={newDraft.isPending}
                onClick={() => newDraft.mutate()}
              >
                New draft
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] 2xl:grid-cols-[minmax(0,1fr)_24rem]">
        <Panel
          id="versions"
          title="Versions"
          description="Per batch, at full precision. One version is in use at a time; signing off a new one retires it."
          className="order-2 xl:order-1"
        >
          {current.length === 0 ? (
            <p className="text-body text-muted">
              Every version is retired, so nothing is in use.{' '}
              {canEdit ? 'Make a new draft to start again.' : ''}
            </p>
          ) : (
            <ol aria-label="Versions in use or waiting" className="flex flex-col">
              {current.map((v) => (
                <VersionBlock
                  key={v.id}
                  recipe={recipe}
                  v={v}
                  canEdit={canEdit}
                  canSignOff={canSignOff}
                  onDialog={setDialog}
                />
              ))}
            </ol>
          )}
          {retired.length > 0 && (
            <details className="rounded-md border border-border px-4 py-2">
              <summary className="cursor-pointer rounded-sm text-sm font-medium text-muted">
                {retired.length === 1 ? '1 retired version' : `${retired.length} retired versions`}
              </summary>
              <ol aria-label="Retired versions" className="flex flex-col pt-4">
                {retired.map((v) => (
                  <VersionBlock
                    key={v.id}
                    recipe={recipe}
                    v={v}
                    canEdit={false}
                    canSignOff={false}
                    onDialog={setDialog}
                  />
                ))}
              </ol>
            </details>
          )}
        </Panel>
        <div className="order-1 flex min-w-0 flex-col gap-6 xl:order-2">
          <CostPanel recipe={recipe} />
          <Panel id="allergens" title="Allergens">
            <AllergenChips allergens={recipe.product.allergens} ingredients={ingredients} />
            {undeclared.length > 0 && (
              // UI5A-1: never "None declared" while the ingredients contain allergens.
              <Note>
                {undeclaredText(recipe.product.allergens, undeclared)}{' '}
                {costed?.status === 'draft'
                  ? 'Signing the recipe off adds them.'
                  : 'Add them to the product.'}{' '}
                <Link
                  to="/catalogue/$productId"
                  params={{ productId: recipe.product.id }}
                  className="font-medium text-primary-text underline underline-offset-4"
                >
                  Open the product
                </Link>
              </Note>
            )}
            {recipe.product.allergen_statement ? (
              <div className="flex flex-col gap-1">
                <p className="text-body text-fg">{recipe.product.allergen_statement}</p>
                <p className="text-sm text-muted">What the baker signed off; labels print it.</p>
              </div>
            ) : (
              <p className="text-sm text-muted">
                No signed statement yet. It’s set when the baker signs off a version.
              </p>
            )}
          </Panel>
        </div>
      </div>

      {dialog?.kind === 'sign-off' && (
        <SignOffDialog recipe={recipe} version={dialog.version} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'retire' && (
        <RetireDialog recipe={recipe} version={dialog.version} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

function RecipeSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading recipe</h1>
      <span className="sr-only">Loading recipe…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-5 w-56" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <Skeleton className="h-96 rounded-lg" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/recipes">
        <ArrowLeft aria-hidden />
        Back to recipes
      </Link>
    </Button>
  );
}

function RecipeError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This recipe"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function RecipeNotFound() {
  useDocumentTitle('Recipe not found');
  return (
    <div className="max-w-2xl">
      <EmptyState
        icon={PackageSearch}
        titleAs="h1"
        title="Recipe not found"
        description="There’s no recipe at this address. It may be in another account, or the link may be mistyped."
        action={<BackButton />}
      />
    </div>
  );
}
